import { inject, Injectable } from '@angular/core';
import { Feature } from 'geojson';
import {
  DataDrivenPropertyValueSpecification,
  LayerSpecification,
  Map,
  Subscription,
} from 'maplibre-gl';
import { MapLayerOrderingService } from '@services/map/map-layer-ordering.service';

export const HIGHLIGHTS_SOURCE_ID = 'highlightSymbols_source';
export const HIGHLIGHT_SYMBOLS_LAYER_ID = 'highlightSymbols_layer';
export const HIGHLIGHT_LINES_LAYER_ID = 'highlightLines_layer';
export const HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID = 'highlightVectorSymbols_layer';
export const HIGHLIGHT_VECTOR_LINES_LAYER_ID = 'highlightVectorLines_layer';

@Injectable({
  providedIn: 'root',
})
export class HighlightService {
  private readonly mapLayerOrderingService = inject(MapLayerOrderingService);
  private readonly activeSubscriptions = new Set<Subscription>();

  highlightFeaturesWithId(
    map: Map,
    featureId: string,
    sourceId: string,
    layerId: string | undefined,
    symbolType: 'circle' | 'point',
  ) {
    const features = map.querySourceFeatures(sourceId, {
      sourceLayer: layerId,
      filter: ['==', 'id', featureId],
    });
    if (symbolType === 'circle') {
      this.setLineAndCircleHighlights(map, features);
    } else {
      this.setLineAndPointHighlights(map, features);
    }

    // while moving the view a part that was not in view before will not yet have the hightlight, so we reload the highlight after each zoom/move event.
    const subscription = map.on('moveend', () => {
      this.highlightFeaturesWithId(map, featureId, sourceId, layerId, symbolType);
      subscription.unsubscribe();
      this.activeSubscriptions.delete(subscription);
    });
    this.activeSubscriptions.add(subscription);
  }

  /**
   * Highlight a feature by id directly from a vector-tile source. The highlight layers read from
   * the same tiles as the feature, so they always match its geometry at every zoom and update
   * automatically as tiles load — unlike the geojson-copy highlights, which freeze the geometry
   * from a single zoom level.
   */
  highlightVectorFeatureById(
    map: Map,
    sourceId: string,
    sourceLayer: string,
    featureId: string,
  ): void {
    this.removeHighlightsFromMap(map);

    const symbolsLayer = this.getVectorSymbolLayer(sourceId, sourceLayer, featureId);
    const linesLayer = this.getVectorLineLayer(sourceId, sourceLayer, featureId);
    map.addLayer(symbolsLayer, this.mapLayerOrderingService.getLayerBeforeId(symbolsLayer.id, map));
    map.addLayer(linesLayer, this.mapLayerOrderingService.getLayerBeforeId(linesLayer.id, map));
  }

  setLineAndPointHighlights(map: Map, features: Feature[]): void {
    this.addToMap(map, features, this.getPointLayer());
  }

  setLineAndCircleHighlights(map: Map, features: Feature[]): void {
    this.addToMap(map, features, this.getCircleLayer());
  }

  private addToMap(map: Map, features: Feature[], symbolsLayer: LayerSpecification) {
    this.removeHighlightsFromMap(map);
    map.addSource(HIGHLIGHTS_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features.map((feature) => {
          return {
            type: 'Feature',
            geometry: feature.geometry,
            properties: feature.properties,
          };
        }),
      },
    });

    map.addLayer(symbolsLayer, this.mapLayerOrderingService.getLayerBeforeId(symbolsLayer.id, map));
    const lineLayer = this.getLineLayer();
    map.addLayer(lineLayer, this.mapLayerOrderingService.getLayerBeforeId(lineLayer.id, map));
  }

  removeHighlightsFromMap(map: Map): void {
    if (map?.getStyle() === undefined) {
      return;
    }

    this.activeSubscriptions.forEach((s) => s.unsubscribe());
    this.activeSubscriptions.clear();

    if (map.getLayer(HIGHLIGHT_LINES_LAYER_ID)) {
      map.removeLayer(HIGHLIGHT_LINES_LAYER_ID);
    }
    if (map.getLayer(HIGHLIGHT_SYMBOLS_LAYER_ID)) {
      map.removeLayer(HIGHLIGHT_SYMBOLS_LAYER_ID);
    }
    if (map.getLayer(HIGHLIGHT_VECTOR_LINES_LAYER_ID)) {
      map.removeLayer(HIGHLIGHT_VECTOR_LINES_LAYER_ID);
    }
    if (map.getLayer(HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID)) {
      map.removeLayer(HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID);
    }
    if (map.getSource(HIGHLIGHTS_SOURCE_ID)) {
      map.removeSource(HIGHLIGHTS_SOURCE_ID);
    }
  }

  getPointLayer(): LayerSpecification {
    return {
      id: HIGHLIGHT_SYMBOLS_LAYER_ID,
      type: 'circle',
      source: HIGHLIGHTS_SOURCE_ID,
      paint: {
        'circle-radius': {
          stops: [
            [10, 8],
            [15, 12],
            [20, 16],
          ],
        },
        'circle-opacity': 0.6,
        'circle-color': 'blue',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
        'circle-stroke-color': 'white',
      } as DataDrivenPropertyValueSpecification<any>,
      filter: ['==', '$type', 'Point'],
    };
  }

  getCircleLayer(): LayerSpecification {
    return {
      id: HIGHLIGHT_SYMBOLS_LAYER_ID,
      type: 'circle',
      source: HIGHLIGHTS_SOURCE_ID,
      paint: {
        'circle-radius': {
          stops: [
            [10, 20],
            [15, 30],
            [20, 40],
          ],
        } as DataDrivenPropertyValueSpecification<any>,
        'circle-opacity': 0,
        'circle-stroke-width': 4,
        'circle-stroke-opacity': 0.75,
        'circle-stroke-color': 'blue',
      },
      filter: ['==', '$type', 'Point'],
    };
  }

  getLineLayer(): LayerSpecification {
    return {
      id: HIGHLIGHT_LINES_LAYER_ID,
      type: 'line',
      source: HIGHLIGHTS_SOURCE_ID,
      paint: {
        'line-color': 'blue',
        'line-width': 3,
      } as DataDrivenPropertyValueSpecification<any>,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      filter: ['==', '$type', 'LineString'],
    };
  }

  private getVectorLineLayer(
    sourceId: string,
    sourceLayer: string,
    featureId: string,
  ): LayerSpecification {
    return {
      id: HIGHLIGHT_VECTOR_LINES_LAYER_ID,
      type: 'line',
      source: sourceId,
      'source-layer': sourceLayer,
      paint: {
        'line-color': 'blue',
        'line-width': 3,
      } as DataDrivenPropertyValueSpecification<any>,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      filter: ['all', ['==', 'id', featureId], ['==', '$type', 'LineString']],
    };
  }

  private getVectorSymbolLayer(
    sourceId: string,
    sourceLayer: string,
    featureId: string,
  ): LayerSpecification {
    return {
      id: HIGHLIGHT_VECTOR_SYMBOLS_LAYER_ID,
      type: 'circle',
      source: sourceId,
      'source-layer': sourceLayer,
      paint: {
        'circle-radius': {
          stops: [
            [10, 20],
            [15, 30],
            [20, 40],
          ],
        } as DataDrivenPropertyValueSpecification<any>,
        'circle-opacity': 0,
        'circle-stroke-width': 4,
        'circle-stroke-opacity': 0.75,
        'circle-stroke-color': 'blue',
      },
      filter: ['all', ['==', 'id', featureId], ['==', '$type', 'Point']],
    };
  }
}
